// src/services/midia.js — os limites da Meta e as checagens de antes do envio.
//
// Out/26. Nasceu de dois relatos: "áudio longo não envia" e "não dá pra
// cancelar um áudio que comecei a gravar". Investigando, o problema de
// fundo era o mesmo nos dois e também no vídeo: o ChatPage mandava o
// arquivo sem olhar NADA — nem tamanho, nem duração — e quem recusava era
// o Nginx (413) ou a Meta (400), já com o upload feito.
//
// Para o agente, isso aparecia como "sumiu". Ele anexava, esperava, e via
// um alerta genérico sem dizer o que fazer. Checar aqui custa milissegundos
// e transforma "não foi possível enviar" em "o vídeo tem 3:20, reduza para
// 2 minutos".

// Tetos da Cloud API da Meta, por tipo. Ela recusa acima disso,
// independente do que a gente faça — então são o fim da linha, não uma
// escolha nossa.
export const LIMITES_META = {
  IMAGE: 5 * 1024 * 1024,
  VIDEO: 16 * 1024 * 1024,
  AUDIO: 16 * 1024 * 1024,
  DOCUMENT: 100 * 1024 * 1024,
};

// Duração máxima de vídeo, em segundos. Decisão de produto, não da Meta:
// um vídeo de 2 min já passa dos 16 MB na qualidade de câmera de celular,
// então avisar pelo TEMPO é mais útil que avisar pelo tamanho — o agente
// sabe cortar segundos, não megabytes.
export const MAX_SEGUNDOS_VIDEO = 120;

// Teto da gravação de voz. Acima disso a gravação para sozinha e o áudio
// fica pronto pra enviar (não é descartado — seria cruel perder cinco
// minutos de fala).
export const MAX_SEGUNDOS_AUDIO = 300;

// Bitrate da gravação de voz.
//
// É a correção do "áudio longo não envia". O MediaRecorder sem
// `audioBitsPerSecond` usa o default do navegador, que no Chrome fica em
// torno de 128 kbps — um áudio de 1 minuto passa de 1 MB, que é exatamente
// o `client_max_body_size` padrão do Nginx. Daí o corte por volta de 40-60
// segundos que o cliente percebeu.
//
// 32 kbps é padrão de voz (o WhatsApp usa perto disso) e deixa o tamanho
// previsível: ~4 KB por segundo, ~1,2 MB em cinco minutos.
export const BITRATE_AUDIO = 32000;

export function formatarTamanho(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

export function formatarDuracao(segundos) {
  const s = Math.max(0, Math.round(segundos || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Duração de um arquivo de vídeo/áudio, em segundos. `null` se não der pra ler.
 *
 * Lê só os metadados — o navegador não baixa o arquivo inteiro pra isso.
 * Devolve `null` em vez de 0 quando falha: 0 passaria por qualquer
 * validação de "menor que o teto" e deixaria o arquivo subir mesmo assim.
 */
export function lerDuracao(file) {
  return new Promise((resolve) => {
    let url;
    const el = document.createElement('video');
    // Timeout porque `onloadedmetadata` simplesmente não dispara pra alguns
    // contêineres (.mov de iPhone antigo, por exemplo). Sem isso a promise
    // ficaria pendurada e o botão de enviar nunca voltaria.
    const encerrar = (valor) => {
      clearTimeout(limite);
      if (url) URL.revokeObjectURL(url);
      resolve(valor);
    };
    const limite = setTimeout(() => encerrar(null), 5000);
    el.preload = 'metadata';
    el.onloadedmetadata = () => {
      const d = el.duration;
      encerrar(Number.isFinite(d) && d > 0 ? d : null);
    };
    el.onerror = () => encerrar(null);
    try {
      url = URL.createObjectURL(file);
      el.src = url;
    } catch {
      encerrar(null);
    }
  });
}

/** Confere um arquivo antes de subir. Devolve a mensagem de erro, ou null.
 *
 * Mensagem em pt-BR e já pronta pra tela — e sempre dizendo o que FAZER,
 * não só o que deu errado.
 */
export async function validarArquivo(file, tipo) {
  if (!file) return 'Nenhum arquivo selecionado.';

  const teto = LIMITES_META[tipo];
  if (teto && file.size > teto) {
    const mb = Math.round(teto / (1024 * 1024));
    const nome = {
      IMAGE: 'A imagem', VIDEO: 'O vídeo',
      AUDIO: 'O áudio', DOCUMENT: 'O arquivo',
    }[tipo] || 'O arquivo';
    return (
      `${nome} tem ${formatarTamanho(file.size)} e o WhatsApp aceita no `
      + `máximo ${mb} MB. ${tipo === 'VIDEO'
        ? 'Corte um trecho ou reduza a qualidade antes de enviar.'
        : 'Reduza o arquivo antes de enviar.'}`
    );
  }

  if (tipo === 'VIDEO') {
    const segundos = await lerDuracao(file);
    // `null` = não consegui ler. Não barra: o teto de tamanho acima já
    // protege, e recusar por não ter conseguido medir puniria o agente por
    // um contêiner que o navegador dele não entende.
    if (segundos !== null && segundos > MAX_SEGUNDOS_VIDEO) {
      return (
        `O vídeo tem ${formatarDuracao(segundos)} e o limite é `
        + `${formatarDuracao(MAX_SEGUNDOS_VIDEO)}. Corte um trecho e tente de novo.`
      );
    }
  }
  return null;
}
